import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from './contexts/LanguageContext';
import './TopicDetailPage.css';
import { isIPadSafari } from './utils/deviceDetection';

const TopicDetailPage = () => {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [isRecording, setIsRecording] = useState(false);
  const [userAnswer, setUserAnswer] = useState('');
  const [showAnswer, setShowAnswer] = useState(false);
  const [isShared, setIsShared] = useState(false);
  const recognitionRef = useRef(null);
  const [recordingTranscript, setRecordingTranscript] = useState('');
  
  // 漂流瓶相关状态
  const [showBottleModal, setShowBottleModal] = useState(false);
  const [bottleAnswer, setBottleAnswer] = useState(null);
  const [bottleLiked, setBottleLiked] = useState(false);
  const [bottleFetched, setBottleFetched] = useState(false); // 跟踪是否已经捞过漂流瓶
  
  // 模拟漂流瓶回答数据库
  const bottleAnswers = [
    {
      id: 1,
      topicId: 1, // 对应GPT-5话题
      location: language === 'en' ? "London, UK" : "英国·伦敦",
      content: "I believe virtual reality will dramatically change how we learn languages. Being immersed in a simulated environment allows for contextual learning that textbooks simply cannot provide. When you're virtually 'in' a foreign country, interacting with AI characters, your brain processes language differently than when just memorizing vocabulary lists.",
      likes: 32
    },
    {
      id: 2,
      topicId: 1, // 对应GPT-5话题
      location: language === 'en' ? "New York, USA" : "美国·纽约",
      content: "From my perspective, the most concerning issue with AI regulation is defining responsibility. If an AI makes a harmful decision, who's accountable - the developers, the company deploying it, or the users? I'd argue we need a framework similar to how we handle pharmaceutical approvals, with rigorous testing requirements before deployment in critical domains.",
      likes: 47
    },
    {
      id: 3,
      topicId: 2, // 对应HIV疫苗话题
      location: language === 'en' ? "Toronto, Canada" : "加拿大·多伦多",
      content: "The HIV vaccine breakthrough represents decades of persistence in medical research. While many people focus only on the final product, I'm particularly impressed by how researchers overcame the virus's unique ability to mutate and hide from the immune system. This success demonstrates why continued funding for 'impossible' medical challenges is absolutely essential.",
      likes: 28
    },
    {
      id: 4,
      topicId: 3, // 对应核污水话题
      location: language === 'en' ? "Sydney, Australia" : "澳大利亚·悉尼",
      content: "Regarding nuclear wastewater disposal, I believe the focus should be on transparent scientific monitoring rather than political posturing. Countries should establish independent international panels to verify safety data and share findings publicly. This approach creates accountability while acknowledging that some difficult environmental decisions must be made collectively.",
      likes: 39
    },
    {
      id: 5,
      topicId: 4, // 对应脑机接口话题
      location: language === 'en' ? "Tokyo, Japan" : "日本·东京",
      content: "When discussing brain implants, we often overlook the subtler ethical questions. Beyond privacy concerns, I worry about how these technologies might alter our fundamental sense of identity and agency. If an implant enhances certain cognitive abilities or influences decision-making, at what point does the technology become inseparable from the self?",
      likes: 51
    },
    {
      id: 6,
      topicId: 5, // 对应比特币话题
      location: language === 'en' ? "Central Singapore" : "新加坡·中央区",
      content: "Blockchain's potential to transform finance is undeniable, but I think we need to acknowledge its significant environmental impact. The energy consumption of proof-of-work systems is simply not sustainable. I've been following the development of more eco-friendly consensus mechanisms, and I believe they represent the future if cryptocurrency is to gain widespread adoption.",
      likes: 38
    },
    {
      id: 7,
      topicId: 6, // 对应元宇宙话题
      location: language === 'en' ? "Seoul, South Korea" : "韩国·首尔",
      content: "The Metaverse real estate boom reminds me of the dot-com bubble. Yes, some early investors will profit enormously, but many will lose significantly. The key difference from physical real estate is that digital scarcity is entirely artificial. I've participated in virtual worlds for years, and I've seen many rise and fall - when a platform loses popularity, those 'valuable' assets become worthless overnight.",
      likes: 43
    }
  ];
  
  // 捞漂流瓶函数
  const fetchBottle = () => {
    // 只选择当前话题ID对应的漂流瓶回答
    const topicBottles = bottleAnswers.filter(bottle => bottle.topicId === parseInt(topicId));
    
    if (topicBottles.length > 0) {
      // 随机获取一个当前话题的漂流瓶回答
      const randomIndex = Math.floor(Math.random() * topicBottles.length);
      const randomAnswer = topicBottles[randomIndex];
      setBottleAnswer({...randomAnswer});
      setBottleLiked(false);
      setShowBottleModal(true);
      setBottleFetched(true); // 标记已经捞过漂流瓶
    } else {
      // 如果当前话题没有对应的漂流瓶回答
      alert(t('no_bottles'));
    }
  };
  
  // 处理点赞
  const handleLikeBottle = () => {
    if (!bottleLiked && bottleAnswer) {
      setBottleAnswer({
        ...bottleAnswer,
        likes: bottleAnswer.likes + 1
      });
      setBottleLiked(true);
    }
  };
  
  // 初始化语音识别
  useEffect(() => {
    // 检查浏览器支持
    window.SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (window.SpeechRecognition) {
      const recognition = new window.SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = language === 'en' ? 'en-US' : 'zh-CN'; // 根据当前选择的语言切换
      
      // 累积的完整转录文本
      let accumulatedTranscript = '';
      
      recognition.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }
        
        // 如果有最终结果，加入累积文本
        if (finalTranscript) {
          accumulatedTranscript += finalTranscript;
        }
        
        // 更新实时转录内容为累积文本加上当前临时文本
        setRecordingTranscript(accumulatedTranscript + interimTranscript);
      };
      
      // 在识别停止时，保存当前的累积转录结果
      recognition.onend = () => {
        // 如果仍在录音状态，自动重新开始录音
        if (isRecording) {
          // 在iPad Safari上，我们使用的是替代方案，不需要在这里重新启动
          if (!isIPadSafari()) {
            try {
              recognition.start();
            } catch (e) {
              console.error('重新启动语音识别失败:', e);
            }
          }
        }
      };
      
      recognition.onerror = (event) => {
        console.error('语音识别错误:', event.error);
        if (event.error === 'no-speech') {
          // 无语音输入时继续监听，而不是停止录音
          return;
        }
        
        // 如果是iPad Safari上的错误，不显示错误提示，因为我们使用了替代方案
        if (!isIPadSafari() && event.error !== 'no-speech') {
          alert(`语音识别错误: ${event.error}`);
        }
        
        if (isRecording) {
          setIsRecording(false);
        }
      };
      
      // 保存识别实例到ref
      recognitionRef.current = recognition;
    }
  }, [language, isRecording]);

  // 当话题ID变化时，重置漂流瓶状态
  useEffect(() => {
    setBottleFetched(false);
    setBottleAnswer(null);
    setBottleLiked(false);
    setShowBottleModal(false);
  }, [topicId]);

  const handleRecordAnswer = () => {
    if (!window.SpeechRecognition && !window.webkitSpeechRecognition) {
      alert(t('browser_not_supported'));
      return;
    }
    
    setIsRecording(true);
    setRecordingTranscript(''); // 清空之前的记录
    
    // 重置累积的转录文本（通过直接修改引用的变量）
    if (recognitionRef.current && typeof recognitionRef.current.onresult === 'function') {
      const originalOnResult = recognitionRef.current.onresult;
      recognitionRef.current.onresult = function(event) {
        // 重置累积文本变量
        let accumulatedTranscript = '';
        // 替换回原始处理函数
        recognitionRef.current.onresult = originalOnResult;
        // 调用原始处理函数
        originalOnResult(event);
      };
    }
    
    try {
      recognitionRef.current.start();
    } catch (e) {
      console.error('启动语音识别失败:', e);
    }
  };

  const handleShareAnswer = () => {
    // 模拟分享回答的功能
    setIsShared(true);
    setTimeout(() => {
      setIsShared(false);
      alert(t('answer_shared'));
    }, 2000);
  };

  const handleFinishRecording = () => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        console.error('停止语音识别失败:', e);
      }
    }
    
    setIsRecording(false);
    // 保存当前的转录内容作为最终答案
    if (recordingTranscript.trim()) {
      setUserAnswer(recordingTranscript.trim());
      setShowAnswer(true);
    } else {
      alert(t('no_speech_detected_part3'));
      setShowAnswer(false);
    }
  };

  // 模拟从API获取话题详情
  const getTopicById = (id) => {
    const topics = [
      {
        id: 1,
        title: language === 'en' ? 'GPT-5 Global Release' : 'GPT-5全球发布',
        source: language === 'en' ? 'OpenAI Official Statement - Tech Daily' : 'OpenAI官方声明 - 科技日报',
        likes: 156,
        tags: language === 'en' ? ['Technology', 'AI', 'Education'] : ['科技', 'AI', '教育'],
        parts: {
          part1: {
            questions: [
              'Do you often use AI tools in your daily life?',
              'What kind of AI technology do you find most useful?',
              'Have you ever tried ChatGPT or similar tools?',
              'Do you think AI will make our lives easier in the future?',
              'Should children be taught how to use AI at school?',
              'What jobs do you think AI can\'t replace?',
              'How has AI changed the way people work?',
              'Would you trust AI to give you medical advice?'
            ]
          },
          part2: {
            question: 'Describe a time when you used AI technology.',
            cues: [
              'What the technology was',
              'How you used it',
              'And how you felt about it'
            ]
          },
          part3: {
            questions: [
              'Do you think AI will replace human jobs?',
              'Should governments regulate AI development?',
              'How can AI improve education systems?'
            ]
          }
        }
      },
      {
        id: 2,
        title: language === 'en' ? 'Long-acting HIV Vaccine Breakthrough' : '长效HIV疫苗问世',
        source: language === 'en' ? 'WHO Annual Medical Research Report - Health News' : 'WHO年度医疗研究报告 - 卫生报',
        likes: 128,
        tags: language === 'en' ? ['Medical', 'Technology', 'Ethics'] : ['医疗', '科技', '伦理'],
        parts: {
          part1: {
            questions: [
              'How important do you think vaccines are?',
              'Have you ever participated in any medical research?',
              'Do you regularly check your health status?',
              'What do you think about free healthcare?',
              'Should governments spend more on medical research?',
              'Have you ever donated blood or organs?',
              'What\'s the most impressive medical breakthrough you\'ve heard of?',
              'Do you think all countries should share medical technologies?'
            ]
          },
          part2: {
            question: 'Describe an important medical breakthrough you know about.',
            cues: [
              'What the breakthrough was',
              'How it works',
              'And why it is significant'
            ]
          },
          part3: {
            questions: [
              'Should healthcare be free for everyone?',
              'How can governments encourage medical research?',
              'What are the ethical issues in vaccine testing?'
            ]
          }
        }
      },
      {
        id: 3,
        title: language === 'en' ? 'Japan Nuclear Wastewater Discharge Controversy' : '日本核污水排放争议',
        source: language === 'en' ? 'Environmental Organizations Joint Statement - Global Times' : '环保组织联合声明 - 环球时报',
        likes: 142,
        tags: language === 'en' ? ['Environment', 'International Affairs', 'Current Events'] : ['环境', '国际事务', '时事'],
        parts: {
          part1: {
            questions: [
              'Is water pollution a problem in your country?',
              'How do you usually get your drinking water?',
              'Have you ever participated in any environmental protection activities?',
              'What\'s the most serious environmental issue in your opinion?',
              'Do you think individuals can help solve environmental problems?',
              'Should companies be punished for causing pollution?',
              'How often do you recycle?',
              'What can schools do to teach children about environmental protection?'
            ]
          },
          part2: {
            question: 'Describe an environmental problem in your country.',
            cues: [
              'What the problem is',
              'What causes it',
              'And how it could be solved'
            ]
          },
          part3: {
            questions: [
              'Who should be responsible for protecting the environment?',
              'Is international cooperation necessary for environmental issues?',
              'How does pollution affect marine life?'
            ]
          }
        }
      },
      {
        id: 4,
        title: language === 'en' ? 'First Human Brain-Computer Interface Implantation' : '人类首例脑机接口植入',
        source: language === 'en' ? 'Neuralink Human Trial Success - Science Weekly' : 'Neuralink人体试验成功 - 科学周刊',
        likes: 115,
        tags: language === 'en' ? ['Technology', 'Medical', 'Ethics'] : ['科技', '医疗', '伦理'],
        parts: {
          part1: {
            questions: [
              'Are you interested in new technologies?',
              'What futuristic technology excites you most?',
              'Would you try a brain implant if it could improve your memory?',
              'Do you think technology is developing too fast?',
              'What modern invention do you think is most important?',
              'Should there be limits on human enhancement technologies?',
              'How often do you read about scientific breakthroughs?',
              'Do you think humans will merge with machines in the future?'
            ]
          },
          part2: {
            question: 'Describe a piece of futuristic technology you find interesting.',
            cues: [
              'What the technology is',
              'How it might be used',
              'And why it interests you'
            ]
          },
          part3: {
            questions: [
              'Should there be limits on human enhancement technologies?',
              'What ethical problems might brain implants cause?',
              'Will humans and machines merge in the future?'
            ]
          }
        }
      },
      {
        id: 5,
        title: language === 'en' ? 'Bitcoin Halving Event' : '比特币减半事件',
        source: language === 'en' ? 'Global Cryptocurrency Market Analysis - Financial Times' : '全球加密货币市场分析 - 金融时报',
        likes: 98,
        tags: language === 'en' ? ['Finance', 'Technology', 'Blockchain'] : ['金融', '科技', '区块链'],
        parts: {
          part1: {
            questions: [
              'Do you often use digital payments?',
              'What do you think about cryptocurrencies?',
              'Have you ever invested in Bitcoin or other digital currencies?',
              'Do you think cash will disappear in the future?',
              'What are the advantages of digital payments?',
              'Should governments regulate cryptocurrencies?',
              'How do you usually save money?',
              'What\'s the biggest risk of using digital currencies?'
            ]
          },
          part2: {
            question: 'Describe a trend in the economy you have noticed.',
            cues: [
              'What the trend is',
              'How it affects people',
              'And whether you think it is positive'
            ]
          },
          part3: {
            questions: [
              'Is cash still important in your country?',
              'What are the risks of digital currencies?',
              'How might blockchain change the financial system?'
            ]
          }
        }
      },
      {
        id: 6,
        title: language === 'en' ? 'Metaverse Virtual Real Estate Boom' : '元宇宙虚拟房产热潮',
        source: language === 'en' ? 'Digital Economy Development Report - Internet Weekly' : '数字经济发展报告 - 互联网周刊',
        likes: 87,
        tags: language === 'en' ? ['Technology', 'Society', 'Virtual Reality'] : ['科技', '社会', '虚拟现实'],
        parts: {
          part1: {
            questions: [
              'Do you spend much time in virtual worlds or games?',
              'Have you ever purchased anything virtual with real money?',
              'Do you think virtual property has real value?',
              'How has the internet changed the concept of ownership?',
              'Would you consider investing in virtual real estate?',
              'Should there be laws protecting virtual property?',
              'How might metaverse affect social interactions?',
              'Do you think people will work in virtual worlds in the future?'
            ]
          },
          part2: {
            question: 'Describe a digital purchase you made that was meaningful to you.',
            cues: [
              'What you purchased',
              'Why you bought it',
              'And how it affected your life'
            ]
          },
          part3: {
            questions: [
              'Will virtual property become as valuable as physical property in the future?',
              'How might metaverse change the real estate industry?',
              'What are the potential risks of investing in virtual assets?'
            ]
          }
        }
      }
    ];
    
    return topics.find(t => t.id === parseInt(id));
  };

  const topic = getTopicById(topicId);
  
  if (!topic) {
    return <div className="not-found">{t('topic_not_found')}</div>;
  }

  return (
    <div className="topic-detail-container">
      <div className="topic-detail-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ← {t('back_topic')}
        </button>
        <h1>{topic.title}</h1>
        <div className="topic-detail-meta">
          <div className="topic-detail-source">{topic.source}</div>
          <div className="topic-detail-likes">❤️ {topic.likes}</div>
        </div>
        <div className="topic-detail-tags">
          {topic.tags.map((tag, index) => (
            <span key={index} className={`topic-detail-tag tag-${language === 'en' ? tag.toLowerCase() : tag}`}>
              {tag}
            </span>
          ))}
        </div>
      </div>
      
      <div className="topic-detail-content">
        <div className="topic-note">
          <span className="note-icon">💡</span>
          <strong>{t('statistics_note')}</strong>
        </div>
        <div className="topic-questions-list">
          <div className="topic-question-item">
            <h3 className="part-title">{t('part2_title_detail')}</h3>
            <div className="topic-question-card">
              <ul className="questions-list">
                <li className="question-item">{topic.parts.part2.question}</li>
                {topic.parts.part2.cues.map((cue, index) => (
                  <li key={index} className="question-item cue-point-item">• {cue}</li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="topic-question-item">
            <h3 className="part-title">{t('part3_title_detail')}</h3>
            <div className="topic-question-card">
              <ul className="questions-list">
                {topic.parts.part3.questions.map((question, index) => (
                  <li key={index} className="question-item">{question}</li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="answer-actions">
            <button 
              className="record-button" 
              onClick={handleRecordAnswer}
              disabled={isRecording}
            >
              {isRecording ? t('recording') : t('input_answer')}
            </button>
            <button 
              className="share-button" 
              onClick={handleShareAnswer}
              disabled={!showAnswer || isShared}
            >
              {isShared ? t('sharing') : t('package_answer')}
            </button>
            <button 
              className="fetch-button"
              onClick={fetchBottle}
              disabled={bottleFetched} // 已经捞过一次就禁用按钮
            >
              {bottleFetched ? t('bottle_retrieved') : t('retrieve_bottle')}
            </button>
          </div>
          
          {isRecording && (
            <div className="recording-container">
              <div className="recording-indicator">
                <div className="recording-pulse"></div>
                {t('recording_prompt')}
              </div>
              <div className="recording-transcript">
                {recordingTranscript ? recordingTranscript : t('listening')}
              </div>
              <div className="recording-instructions">
                <p>{t('recording_instruction1')}</p>
                <p>{t('recording_instruction2')}</p>
                <p>{t('recording_instruction3')}</p>
                <p>{t('recording_instruction4')}</p>
              </div>
              <button className="stop-recording" onClick={handleFinishRecording}>
                {t('finish')}
              </button>
            </div>
          )}
          
          {showAnswer && !isRecording && (
            <div className="user-answer-container">
              <h3>{t('your_answer')}</h3>
              <div className="user-answer-content">
                {userAnswer}
              </div>
            </div>
          )}
        </div>
        
        <div className="top-answers">
          <h3>{t('top_rated_answers')}</h3>
          <div className="answer-card">
            <div className="answer-header">
              <div className="user-info">
                <span className="avatar">👤</span>
                <span className="username"></span>
              </div>
              <div className="answer-likes">❤️ 45</div>
            </div>
            <div className="answer-content">
              {topicId === '1' && (
                "I strongly believe governments must implement thoughtful regulations for AI, though these should be carefully balanced. In my view, while AI like GPT-5 offers incredible benefits for education and healthcare, the potential risks are simply too significant to ignore. For instance, just last month we saw how AI-generated deepfakes caused panic in financial markets. That said, I would argue that regulations should focus on specific high-risk applications rather than blanket restrictions. From my perspective, the ideal approach would combine strict safety standards for sensitive areas like medical diagnosis with more flexible rules for creative uses. Personally, I think the EU's new AI Act provides an excellent model that other governments could follow."
              )}
              {topicId === '2' && (
                "In my opinion, universal healthcare is ultimately the fairest system, though I acknowledge the practical challenges. I feel particularly strongly about this when considering breakthroughs like the new HIV vaccine - such medical advances should benefit all of society, not just those who can afford them. However, I must admit that completely free healthcare may not be realistic in all countries. From my observations, systems like the UK's NHS show that taxpayer-funded healthcare can work well, but they require careful management. If I had to propose a solution, I would suggest a hybrid model where basic care is free, with optional private insurance for additional services."
              )}
              {topicId === '3' && (
                "I'm completely convinced that international cooperation is essential for environmental protection. My reasoning is that pollution doesn't respect national borders - as we've seen with the Fukushima wastewater situation, one country's actions can affect entire regions. From my perspective, existing agreements like the Paris climate accord prove that global cooperation is possible, though I recognize these processes can be slow. If we want to prevent future environmental crises, I believe we need stronger international laws with proper enforcement mechanisms. Personally, I'd like to see more funding for joint scientific research on these issues."
              )}
              {topicId === '4' && (
                "In my view, brain-computer interfaces raise some profound ethical dilemmas that society isn't yet prepared to handle. I'm particularly concerned about privacy issues - imagine if corporations could access our thoughts or memories without consent. Additionally, I worry these technologies might exacerbate social inequality. If only the wealthy can afford cognitive enhancements, it could create an even wider gap between rich and poor. From my perspective, we urgently need new laws like Chile's neurorights legislation to protect mental privacy. I strongly feel that ethical considerations should keep pace with technological progress."
              )}
              {topicId === '5' && (
                "I'm convinced blockchain will fundamentally transform finance, though the transition may take time. In my opinion, the technology's greatest benefit is enabling faster, more transparent transactions - I've personally used crypto for international payments that completed in minutes rather than days. However, I must acknowledge that volatility remains a major obstacle to widespread adoption. From my observations, the most promising development is central bank digital currencies that combine blockchain's efficiency with government backing. If implemented properly, I believe these could make financial systems both more innovative and more stable."
              )}
              {topicId === '6' && (
                "Personally, I believe Metaverse real estate is a fascinating but highly speculative investment. On one hand, virtual land in platforms like Decentraland has sold for millions, suggesting strong demand from tech companies and investors. Early adopters argue that as VR technology improves, these digital spaces could become valuable for advertising, social events, and even virtual workplaces. However, I'm skeptical about its long-term stability. Unlike physical property, virtual land has no inherent scarcity—platforms could always create more space, potentially crashing prices. We've already seen prices drop sharply after the crypto market decline in 2022. In my view, unless you fully understand blockchain risks and have disposable income, it's wiser to treat it as an experimental investment rather than a reliable asset."
              )}
            </div>
          </div>
          <div className="answer-note">
            {t('reference_note')}
          </div>
        </div>
      </div>
      
      {/* 漂流瓶弹窗 */}
      {showBottleModal && bottleAnswer && (
        <div className="bottle-modal-overlay">
          <div className="bottle-modal">
            <div className="bottle-modal-header">
              <h3 className="bottle-success-message">
                {t('found_bottle')}
              </h3>
              <button 
                className="bottle-close-button"
                onClick={() => setShowBottleModal(false)}
              >
                ×
              </button>
            </div>
            <div className="bottle-content">
              <div className="bottle-user-info">
                <span className="bottle-avatar">👤</span>
                <span className="bottle-username">{bottleAnswer.location}</span>
              </div>
              <div className="bottle-answer">
                {bottleAnswer.content}
              </div>
              <div className="bottle-likes">
                <button 
                  className={`like-button ${bottleLiked ? 'liked' : ''}`}
                  onClick={handleLikeBottle}
                  disabled={bottleLiked}
                >
                  <span className="like-icon">❤️</span>
                  <span className="like-count">{bottleAnswer.likes}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopicDetailPage; 